/**
 * Server-Sent Events over fetch() — the browser's EventSource cannot send an Authorization header,
 * so GET /events/stream is read as a ReadableStream and parsed here.
 *
 * SseParser follows the WHATWG event-stream rules: lines end in CRLF, LF or CR (also when a CRLF
 * is split across chunks); a leading BOM is dropped; ':' lines are comments (the API's ': ping'
 * heartbeat); `data` lines of one event are joined with '\n'; `id` sets the last event id (kept
 * across events, ignored when it contains NUL); `retry` is remembered; a blank line dispatches;
 * an event without data is not dispatched; an unfinished event at the end of the stream is
 * discarded.
 */
export interface SseMessage {
  /** `event:` field, 'message' when absent. */
  type: string;
  data: string;
  /** The last event id at dispatch time ('' when none was ever set). */
  lastEventId: string;
}

export class SseParser {
  private buffer = '';
  private skipLeadingLF = false;
  private started = false;
  private dataLines: string[] = [];
  private eventType = '';
  private eventId = '';
  /** Reconnection time the server asked for (`retry:` field), ms. */
  retry: number | null = null;

  constructor(
    private readonly onMessage: (message: SseMessage) => void,
    private readonly onComment?: (text: string) => void,
    initialLastEventId = '',
  ) {
    this.eventId = initialLastEventId;
  }

  get lastEventId(): string {
    return this.eventId;
  }

  push(chunk: string): void {
    let text = chunk;
    if (!this.started && text.length > 0) {
      this.started = true;
      if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
    }
    if (this.skipLeadingLF && text.length > 0) {
      if (text[0] === '\n') text = text.slice(1);
      this.skipLeadingLF = false;
    }
    this.buffer += text;

    let start = 0;
    const buf = this.buffer;
    for (let i = 0; i < buf.length; i += 1) {
      const c = buf[i];
      if (c !== '\n' && c !== '\r') continue;
      this.line(buf.slice(start, i));
      if (c === '\r') {
        if (i + 1 < buf.length) {
          if (buf[i + 1] === '\n') i += 1;
        } else {
          this.skipLeadingLF = true; // a CRLF split across two chunks
        }
      }
      start = i + 1;
    }
    this.buffer = buf.slice(start);
  }

  /** End of stream: an event that was not terminated by a blank line is discarded. */
  end(): void {
    this.buffer = '';
    this.dataLines = [];
    this.eventType = '';
  }

  private line(line: string): void {
    if (line === '') {
      this.dispatch();
      return;
    }
    if (line[0] === ':') {
      this.onComment?.(line.slice(1).replace(/^ /, ''));
      return;
    }
    const colon = line.indexOf(':');
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? '' : line.slice(colon + 1);
    if (value[0] === ' ') value = value.slice(1);
    switch (field) {
      case 'event':
        this.eventType = value;
        break;
      case 'data':
        this.dataLines.push(value);
        break;
      case 'id':
        if (!value.includes('\u0000')) this.eventId = value;
        break;
      case 'retry':
        if (/^\d+$/.test(value)) this.retry = Number(value);
        break;
      default:
        break; // unknown fields are ignored
    }
  }

  private dispatch(): void {
    if (this.dataLines.length === 0) {
      this.eventType = '';
      return;
    }
    const message: SseMessage = {
      type: this.eventType || 'message',
      data: this.dataLines.join('\n'),
      lastEventId: this.eventId,
    };
    this.dataLines = [];
    this.eventType = '';
    this.onMessage(message);
  }
}

export type LiveStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'stopped';

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface LiveStreamOptions {
  url: string;
  /** The current access token (null = signed out: the stream stops). */
  getToken: () => string | null;
  /** Refresh the tokens after a 401: false = the API refused (signed out), throws = try later. */
  refresh: () => Promise<boolean>;
  onEvent: (type: string, data: unknown, id: string) => void;
  onStatus?: (status: LiveStatus) => void;
  fetchImpl?: FetchLike;
  /** First reconnect delay (1 s) and the cap (30 s); doubles per failed attempt. */
  minBackoffMs?: number;
  maxBackoffMs?: number;
  /** Heartbeat interval until `ready` says otherwise (the API default, 15 s). */
  heartbeatMs?: number;
  /** 0..1, for jitter (tests pass () => 1). */
  random?: () => number;
}

/**
 * One live connection to GET /events/stream: Authorization header, Last-Event-ID on reconnect
 * (the API replays what was missed, or sends `resync`), exponential backoff 1–30 s with a little
 * jitter, an idle watchdog (no byte — not even the ': ping' heartbeat — for 2× the heartbeat
 * interval: reconnect), one token refresh on 401.
 */
export class LiveStream {
  private readonly fetchImpl: FetchLike;
  private readonly minBackoff: number;
  private readonly maxBackoff: number;
  private readonly random: () => number;
  private heartbeatMs: number;
  private lastEventId = '';
  private failures = 0;
  private stopped = true;
  private controller: AbortController | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private current: LiveStatus = 'idle';

  constructor(private readonly opts: LiveStreamOptions) {
    this.fetchImpl = opts.fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
    this.minBackoff = opts.minBackoffMs ?? 1_000;
    this.maxBackoff = opts.maxBackoffMs ?? 30_000;
    this.heartbeatMs = opts.heartbeatMs ?? 15_000;
    this.random = opts.random ?? Math.random;
  }

  get status(): LiveStatus {
    return this.current;
  }

  /** The id the next reconnect sends as Last-Event-ID. */
  get resumeId(): string {
    return this.lastEventId;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    this.controller?.abort();
    this.controller = null;
    this.setStatus('stopped');
  }

  /** Skip the backoff (the browser came back online, the tab became visible). */
  reconnectNow(): void {
    if (this.stopped || this.current === 'open' || this.current === 'connecting') return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.failures = 0;
    this.controller?.abort();
    void this.connect();
  }

  /** Delay before reconnect attempt n (0-based): 1, 2, 4, 8, 16, 30, 30 … s, with jitter. */
  backoffDelay(attempt: number): number {
    const base = Math.min(this.maxBackoff, this.minBackoff * 2 ** Math.min(attempt, 16));
    return Math.max(this.minBackoff, Math.round(base * (0.8 + 0.2 * this.random())));
  }

  private setStatus(status: LiveStatus): void {
    if (this.current === status) return;
    this.current = status;
    this.opts.onStatus?.(status);
  }

  private clearTimers(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.watchdog) clearTimeout(this.watchdog);
    this.retryTimer = null;
    this.watchdog = null;
  }

  private open(signal: AbortSignal): Promise<Response> {
    const token = this.opts.getToken();
    const headers: Record<string, string> = { accept: 'text/event-stream' };
    if (token) headers.authorization = `Bearer ${token}`;
    if (this.lastEventId) headers['last-event-id'] = this.lastEventId;
    return this.fetchImpl(this.opts.url, { method: 'GET', headers, signal, cache: 'no-store' });
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;
    if (!this.opts.getToken()) {
      this.stop();
      return;
    }
    this.setStatus(this.failures === 0 && this.current !== 'reconnecting' ? 'connecting' : 'reconnecting');
    const controller = new AbortController();
    this.controller = controller;
    try {
      let res = await this.open(controller.signal);
      if (res.status === 401) {
        await res.body?.cancel().catch(() => undefined);
        const refreshed = await this.opts.refresh();
        if (!refreshed) {
          this.stop(); // signed out
          return;
        }
        if (this.stopped || controller.signal.aborted) return;
        res = await this.open(controller.signal);
      }
      if (!res.ok || !res.body) {
        await res.body?.cancel().catch(() => undefined);
        throw new Error(`event stream HTTP ${res.status}`);
      }
      await this.read(res.body, controller);
    } catch {
      // network failure, HTTP error, refresh failure or our own abort (watchdog / stop)
    } finally {
      if (this.controller === controller) {
        if (this.watchdog) clearTimeout(this.watchdog);
        this.watchdog = null;
        this.controller = null;
      }
    }
    // The stream ended, failed or the watchdog aborted it: reconnect with backoff — unless the
    // panel stopped it or reconnectNow() already opened a newer connection.
    if (!this.stopped && this.controller === null) this.scheduleReconnect();
  }

  private async read(body: ReadableStream<Uint8Array>, controller: AbortController): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    const parser = new SseParser((m) => this.handle(m), undefined, this.lastEventId);
    const onAbort = (): void => {
      void reader.cancel().catch(() => undefined);
    };
    controller.signal.addEventListener('abort', onAbort, { once: true });
    this.armWatchdog(controller);
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done || controller.signal.aborted) break;
        this.armWatchdog(controller);
        parser.push(decoder.decode(value, { stream: true }));
      }
      parser.push(decoder.decode());
      parser.end();
    } finally {
      controller.signal.removeEventListener('abort', onAbort);
    }
  }

  private armWatchdog(controller: AbortController): void {
    if (this.watchdog) clearTimeout(this.watchdog);
    this.watchdog = setTimeout(() => {
      this.watchdog = null;
      controller.abort(); // no byte for 2× the heartbeat: the connection is dead
    }, 2 * this.heartbeatMs);
  }

  private handle(message: SseMessage): void {
    if (message.lastEventId) this.lastEventId = message.lastEventId;
    let data: unknown = null;
    try {
      data = message.data ? JSON.parse(message.data) : null;
    } catch {
      return; // not ours
    }
    if (message.type === 'ready') {
      const hb = (data as { heartbeatMs?: unknown } | null)?.heartbeatMs;
      if (typeof hb === 'number' && hb > 0) this.heartbeatMs = hb;
      this.failures = 0;
      this.setStatus('open');
      if (this.controller) this.armWatchdog(this.controller);
    }
    this.opts.onEvent(message.type, data, message.lastEventId);
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.retryTimer) return;
    const delay = this.backoffDelay(this.failures);
    this.failures += 1;
    this.setStatus('reconnecting');
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.connect();
    }, delay);
  }
}
